<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">

        <!-- Toggle Filter Button (top-left corner) -->
        <button @click="toggleFilter"
            class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
            <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
        </button>

        <!-- Sliding Sidebar Filter -->
        <transition name="slide">
            <aside v-show="showFilter"
                class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r">
                <div class="p-6 border-b">
                    <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
                        <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
                        Filters
                    </h2>
                </div>
                <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Program</label>
                        <select v-model="filterByProgram" class="w-full px-2 py-1 border outline-none">
                            <option value="">All Programs</option>
                            <option :value="program" v-for="(program, index) in programs" :key="index">
                                {{ program }}
                            </option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Term Code</label>
                        <select v-model="termCode" class="w-full px-2 py-1 border outline-none">
                            <option value="202610">202610</option>
                        </select>
                    </div>
                </div>

                <!-- Apply Button -->
                <div class="p-6 flex justify-center space-x-6 border-t">
                    <button @click.stop="clearFilters"
                        class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold">
                        Clear
                    </button>
                    <button @click.stop="applyFilters"
                        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold">
                        Apply
                    </button>
                </div>
            </aside>
        </transition>

        <div class="container mx-auto px-4 sm:px-8 py-6 space-y-8">
            <div class="px-8 flex flex-col h-full w-full py-8">
                <div class="w-full bg-white rounded-2xl border shadow-lg px-6 md:px-8 py-4 flex flex-col">
                    <div class="flex items-center text-sm font-light flex-wrap">
                        <router-link to="/dashboard" class="opacity-50 hover:opacity-100">Dashboard</router-link>
                        <span class="mx-2">/</span>
                        <router-link to="/fchs" class="opacity-50 hover:opacity-100">FCHS</router-link>
                        <span class="mx-2">/</span>
                        <router-link to="/fchs-al-ain" class="opacity-50 hover:opacity-100">Al Ain</router-link>
                        <span class="mx-2">/</span>
                        <router-link to="/campus" class="opacity-50 hover:opacity-100">Campus</router-link>
                        <span class="mx-2">/</span>
                        <router-link to="/people" class="opacity-50 hover:opacity-100">People</router-link>
                        <span class="mx-2">/</span>
                        <span>Academics</span>
                    </div>
                    <div class="font-bold">Academics</div>
                </div>

                <!-- Summary Cards -->
                <div class="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <!-- Total Faculty Card -->
                    <div class="bg-white p-4 shadow rounded-lg">
                        <h2 class="text-sm font-medium text-gray-500">
                            <span class="mr-1">👥</span>
                            Total Faculty
                        </h2>
                        <p class="text-2xl font-semibold text-gray-800">
                            {{ facultySummary ? facultySummary.total_count : 0 }}
                        </p>
                    </div>
                    <!-- Program Cards -->
                    <div v-for="(count, category) in categoryCounts" :key="category"
                        class="bg-white p-4 shadow rounded-lg">
                        <h2 class="text-sm font-medium text-gray-500">
                            <span class="mr-1">{{ emojiMap[category] || '📁' }}</span>
                            {{ category }}
                        </h2>
                        <p class="text-2xl font-semibold text-gray-800">{{ count }}</p>
                    </div>
                </div>

                <!-- Charts -->
                <div class="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ProgramDistributionChart :categoryCounts="categoryCounts" :powerBiColors="powerBiColors" />
                    <CourseTypeDistributionChart :data="courseTypeDistribution" :loading="facultySummaryLoading" />
                    <!-- <FacultyCountByProgramChart :categoryCounts="categoryCounts" :powerBiColors="powerBiColors" /> -->
                </div>

                <!-- Faculty Loads Charts -->
                <div class="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <HrPerWeekChart :hrPerWeekData="hrPerWeekData" :powerBiColors="powerBiColors" />
                    <TotalPerTermChart :totalPerTermData="totalPerTermData" :powerBiColors="powerBiColors" />
                </div>

                <!-- Faculty Course Visualizations -->
                <div class="mt-8 grid grid-cols-1 gap-6">
                    <TopStudentsChart :data="studentsPerCourseCode" :powerBiColors="powerBiColors"
                        :fetchOtherCourseCodes="fetchOtherCourseCodes" />
                </div>

                <!-- Faculty Course Summary -->
                <div class="mt-8">
                    <FacultyCoursesSummary :mostCourses="facultyWithMostCourses" :leastCourses="facultyWithLeastCourses"
                        :loading="facultySummaryLoading" />
                </div>

                <!-- Class Timing Distribution -->
                <div class="mt-8">
                    <ClassTimingDistributionChart :timingData="classTimingDistribution"
                        :powerBiColors="powerBiColors" />
                </div>

                <!-- Day Distribution -->
                <div class="mt-8">
                    <DayDistributionChart :data="dayDistribution" :powerBiColors="powerBiColors" />
                </div>

                <!-- Room Utilization -->
                <div class="mt-8">
                    <RoomUtilizationChart :data="roomUtilization" :powerBiColors="powerBiColors"
                        :fetchOtherRooms="fetchOtherRooms" />
                </div>

                <!-- People Page -->
                <section class="mt-8 h-full w-full flex flex-col bg-white rounded-2xl border shadow-lg p-6 md:p-8">


                    <div class="flex justify-between items-center flex-wrap gap-4">
                        <h1 class="text-2xl font-semibold text-gray-800 flex items-center">
                            <font-awesome-icon :icon="['fas', 'chalkboard-teacher']" class="text-blue-600 mr-2" />
                            Academics
                        </h1>
                        <div class="w-full sm:w-auto">
                            <search ref="searchRef" @on-search="onSearch" class-list="px-4 h-8"></search>
                        </div>
                    </div>


                    <div class="overflow-x-auto mt-6">
                        <table class="min-w-full divide-y divide-gray-200 text-sm">
                            <thead class="bg-gray-100 text-gray-600 font-medium">
                                <tr>
                                    <th class="px-4 py-3 text-left w-12">#</th>
                                    <th class="px-4 py-3 text-left">Name</th>
                                    <th class="px-4 py-3 text-left">Job Title</th>
                                    <th class="px-4 py-3 text-left">E-Mail Address</th>
                                    <th class="px-4 py-3 text-left">Program</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100 text-gray-700">
                                <tr v-for="(item, index) in faculties" :key="index"
                                    class="hover:bg-blue-50 transition duration-200">
                                    <td class="px-4 py-3 font-medium text-gray-500">{{ paginatorInfo.firstItem + index
                                        }}</td>
                                    <td class="px-4 py-3">{{ item.name }}</td>
                                    <td class="px-4 py-3">{{ item.job_title }}</td>
                                    <td class="px-4 py-3 text-blue-700">
                                        <a v-if="item.email" :href="`mailto:${item.email}`" class="hover:underline">
                                            {{ item.email }}
                                        </a>
                                        <span v-else class="text-gray-400 italic">—</span>
                                    </td>
                                    <td class="px-4 py-3">{{ item.program }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="mt-4">
                        <pagination :paginatorInfo="paginatorInfo" :setPage="setPage"></pagination>
                    </div>
                </section>
            </div>
        </div>
        <div class="h-full flex justify-center items-center" v-if="isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>
    </div>
</template>

<script>
import { gql } from 'graphql-tag';
import {
    ProgramDistributionChart,
    // FacultyCountByProgramChart,
    HrPerWeekChart, TotalPerTermChart, CourseTypeDistributionChart, TopStudentsChart, FacultyCoursesSummary, ClassTimingDistributionChart, DayDistributionChart, RoomUtilizationChart
} from './charts';
import Pagination from "../../../../../components/shared/pagination.vue";
import Search from "../../../../../components/shared/search.vue";

export default {
    name: "academics-index",
    components: {
        ProgramDistributionChart,
        // FacultyCountByProgramChart,
        HrPerWeekChart,
        TotalPerTermChart,
        CourseTypeDistributionChart,
        TopStudentsChart,
        FacultyCoursesSummary,
        ClassTimingDistributionChart,
        DayDistributionChart,
        RoomUtilizationChart,
        Pagination,
        Search
    },
    data() {
        return {
            programs: [],
            filterByProgram: "",
            showFilter: false,
            powerBiColors: [
                "#01B8AA", "#374649", "#FD625E", "#F2C80F", "#5F6B6D",
                "#8AD4EB", "#FE9666", "#A66999", "#3599B8", "#DFBFBF",
                "#95A5A6",
            ],
            faculties: [],
            paginatorInfo: {},
            facultySummary: null,
            first: 10,
            page: 1,
            isLoading: false,
            facultyType: 'academic',
            entity: 'FCHS',
            campus: 'FCHS-ALAIN',
            searchText: "",
            termCode: '202610',
        };
    },
    created() {
        this.fetchFaculties();
        this.fetchFacultySummary();
    },
    apollo: {
        facultyPrograms() {
            return {
                query: gql`query GetFacultyPrograms($entity: String, $campus: String, $faculty_type: String) {
                    facultyPrograms(entity: $entity, campus: $campus, faculty_type: $faculty_type) {
                        error
                        error_code
                        message
                        data
                    }
                }`,
                variables() {
                    return {
                        entity: this.entity,
                        campus: this.campus,
                        faculty_type: this.facultyType
                    }
                },
                fetchPolicy: 'cache-and-network',
                result({ data }) {
                    if (data && data.facultyPrograms && !data.facultyPrograms.error) {
                        // Programs are already a simple array of strings from the backend
                        this.programs = data.facultyPrograms.data;
                    }
                }
            }
        }
    },
    watch: {
        searchText() {
            this.page = 1;
            this.fetchFaculties();
        },
    },
    computed: {
        emojiMap() {
            return {
                'Nursing': '💉',
                'Pharmacy': '💊',
                'Physiotherapy': '🦵',
                'Radiology': '🩻',
                'EH': '🚑',
                'HCA': '🩺',
                'GR': '📘',
                'Other': '📁'
            };
        },
        categoryCounts() {
            if (!this.facultySummary || !this.facultySummary.program_distribution) {
                return {};
            }
            return this.facultySummary.program_distribution.reduce((acc, item) => {
                acc[item.program] = item.count;
                return acc;
            }, {});
        },
        hrPerWeekData() {
            if (!this.facultySummary || !this.facultySummary.hr_per_week_distribution) {
                return [];
            }
            return this.facultySummary.hr_per_week_distribution;
        },
        totalPerTermData() {
            if (!this.facultySummary || !this.facultySummary.total_per_term_distribution) {
                return [];
            }
            return this.facultySummary.total_per_term_distribution;
        },
        facultyWithMostCourses() {
            if (!this.facultySummary || !this.facultySummary.faculty_with_most_courses) {
                return [];
            }
            return this.facultySummary.faculty_with_most_courses;
        },
        facultyWithLeastCourses() {
            if (!this.facultySummary || !this.facultySummary.faculty_with_least_courses) {
                return [];
            }
            return this.facultySummary.faculty_with_least_courses;
        },
        studentsPerCourseCode() {
            if (!this.facultySummary || !this.facultySummary.students_per_course_code) {
                return [];
            }
            return this.facultySummary.students_per_course_code;
        },
        courseTypeDistribution() {
            if (!this.facultySummary || !this.facultySummary.course_type_distribution) {
                return [];
            }
            return this.facultySummary.course_type_distribution;
        },
        classTimingDistribution() {
            if (!this.facultySummary || !this.facultySummary.class_timing_distribution) {
                return null;
            }
            return this.facultySummary.class_timing_distribution;
        },
        dayDistribution() {
            if (!this.facultySummary || !this.facultySummary.day_distribution) {
                return null;
            }
            return this.facultySummary.day_distribution;
        },
        roomUtilization() {
            if (!this.facultySummary || !this.facultySummary.room_utilization) {
                return null;
            }
            return this.facultySummary.room_utilization;
        },
    },
    methods: {
        setPage(page) {
            this.page = page;
            this.fetchFaculties();
        },
        onSearch(searchText) {
            this.searchText = searchText;
        },
        async fetchFaculties() {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: gql`query GetAllFaculties($first: Int!, $page: Int!, $entity: String, $campus: String, $faculty_type: String, $program: String, $search: String) {
                        faculties(first: $first, page: $page, entity: $entity, campus: $campus, faculty_type: $faculty_type, program: $program, search: $search) {
                            error
                            error_code
                            message
                            paginatorInfo {
                                count
                                currentPage
                                firstItem
                                hasMorePages
                                lastItem
                                lastPage
                                perPage
                                total
                            }
                            data {
                                id
                                name
                                email
                                entity
                                campus
                                job_title
                                program
                                faculty_type
                            }
                        }
                    }`,
                    variables: {
                        first: this.first,
                        page: this.page,
                        entity: this.entity,
                        campus: this.campus,
                        faculty_type: this.facultyType,
                        program: this.filterByProgram,
                        search: this.searchText
                    },
                    fetchPolicy: 'network-only'
                });

                if (response.data.faculties && !response.data.faculties.error) {
                    this.faculties = response.data.faculties.data;
                    this.paginatorInfo = response.data.faculties.paginatorInfo;
                }
            } catch (error) {
                console.error("Failed to fetch faculties:", error);
            }
            this.isLoading = false;
        },
        async fetchOtherRooms(offset = 0, limit = 50) {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: gql`query GetOtherRooms($entity: String, $campus: String, $faculty_type: String, $program: String, $term_code: String, $offset: Int, $limit: Int) {
                        otherRooms(entity: $entity, campus: $campus, faculty_type: $faculty_type, program: $program, term_code: $term_code, offset: $offset, limit: $limit) {
                            error
                            error_code
                            message
                            data {
                                rooms {
                                    room
                                    class_count
                                    room_capacity
                                    total_max_enrollment
                                    total_enrolled_students
                                }
                                total_count
                                has_more
                            }
                        }
                    }`,
                    variables: {
                        entity: this.entity,
                        campus: this.campus,
                        faculty_type: this.facultyType,
                        program: this.filterByProgram,
                        term_code: this.termCode,
                        offset: offset,
                        limit: limit
                    },
                    fetchPolicy: 'network-only'
                });

                this.isLoading = false;

                if (response.data.otherRooms && !response.data.otherRooms.error) {
                    return response.data.otherRooms.data;
                } else {
                    console.error('Other rooms error:', response.data.otherRooms);
                    return null;
                }
            } catch (error) {
                this.isLoading = false;
                console.error("Failed to fetch other rooms:", error);
                return null;
            }
        },
        async fetchOtherCourseCodes() {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: gql`query GetOtherCourseCodes($entity: String, $campus: String, $faculty_type: String, $program: String, $term_code: String) {
                        otherCourseCodes(entity: $entity, campus: $campus, faculty_type: $faculty_type, program: $program, term_code: $term_code) {
                            error
                            error_code
                            message
                            data {
                                course_codes {
                                    course_code
                                    total_students
                                }
                            }
                        }
                    }`,
                    variables: {
                        entity: this.entity,
                        campus: this.campus,
                        faculty_type: this.facultyType,
                        program: this.filterByProgram,
                        term_code: this.termCode
                    },
                    fetchPolicy: 'network-only'
                });

                this.isLoading = false;

                if (response.data.otherCourseCodes && !response.data.otherCourseCodes.error) {
                    return response.data.otherCourseCodes.data;
                } else {
                    console.error('Other course codes error:', response.data.otherCourseCodes);
                    return null;
                }
            } catch (error) {
                this.isLoading = false;
                console.error("Failed to fetch other course codes:", error);
                return null;
            }
        },
        async fetchFacultySummary() {
            try {
                const response = await this.$apollo.query({
                    query: gql`query GetFacultySummary($entity: String, $campus: String, $faculty_type: String, $program: String, $term_code: String) {
                        facultySummary(entity: $entity, campus: $campus, faculty_type: $faculty_type, program: $program, term_code: $term_code) {
                            error
                            error_code
                            message
                            data {
                                total_count
                                program_distribution {
                                    program
                                    count
                                }
                                hr_per_week_distribution {
                                    program
                                    total_hr_per_week
                                }
                                total_per_term_distribution {
                                    program
                                    total_per_term
                                }
                                faculty_with_most_courses {
                                    name
                                    program
                                    course_count
                                }
                                faculty_with_least_courses {
                                    name
                                    program
                                    course_count
                                }
                                students_per_course_code {
                                    course_code
                                    total_students
                                }
                                course_type_distribution {
                                    course_type
                                    course_count
                                }
                                class_timing_distribution {
                                    peak_vs_non_peak {
                                        category
                                        class_count
                                        percentage
                                    }
                                    time_of_day {
                                        category
                                        class_count
                                        percentage
                                    }
                                    hourly_distribution {
                                        hour
                                        class_count
                                        label
                                    }
                                    total_classes
                                    summary {
                                        peak_percentage
                                        morning_percentage
                                        afternoon_percentage
                                        evening_percentage
                                    }
                                }
                                day_distribution {
                                    days {
                                        day_code
                                        day_name
                                        class_count
                                        percentage
                                    }
                                    total_classes
                                    summary {
                                        weekday_classes
                                        weekend_classes
                                        most_busy_day {
                                            day_code
                                            day_name
                                            class_count
                                            percentage
                                        }
                                        least_busy_day {
                                            day_code
                                            day_name
                                            class_count
                                            percentage
                                        }
                                    }
                                }
                                room_utilization {
                                    summary {
                                        total_classes
                                        total_rooms
                                        total_room_capacity
                                        total_max_enrollment
                                        total_enrolled_students
                                        capacity_utilization_percentage
                                        enrollment_utilization_percentage
                                        total_seat_availability
                                    }
                                    top_utilized_rooms {
                                        room
                                        class_count
                                    }
                                    capacity_ranges {
                                        small
                                        medium
                                        large
                                    }
                                }
                            }
                        }
                    }`,
                    variables: {
                        entity: this.entity,
                        campus: this.campus,
                        faculty_type: this.facultyType,
                        program: this.filterByProgram,
                        term_code: this.termCode
                    },
                    fetchPolicy: 'network-only'
                });

                if (response.data.facultySummary && !response.data.facultySummary.error) {
                    this.facultySummary = response.data.facultySummary.data;
                }
            } catch (error) {
                console.error("Failed to fetch faculty summary:", error);
            }
        },
        toggleFilter() {
            this.showFilter = !this.showFilter;
        },
        clearFilters() {
            this.toggleFilter();
            this.$refs.searchRef.clearInput();
            this.filterByProgram = "";
            this.termCode = '202610';
            this.page = 1;
            this.fetchFaculties();
            this.fetchFacultySummary();
        },
        applyFilters() {
            this.toggleFilter();
            this.page = 1;
            this.fetchFaculties();
            this.fetchFacultySummary();
        }
    },
};
</script>

<style lang="scss" scoped>
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(-100%);
}
</style>
